import React from 'react';
import { motion } from 'framer-motion';
import { Home, Building2, Users, FileText, Scale, CheckCircle } from 'lucide-react';

export default function ServicesPage() {
  const services = [
    {
      icon: <Home className="w-12 h-12" />,
      title: 'Buying & Selling Made Easy',
      description: 'Finding the right property or managing one should not be stressful. We make your real estate journey smooth, profitable, and worry-free.',
      features: [
        'Flats, villas, and apartments for every budget',
        'Residential, commercial, agricultural, and plotted lands',
        'Expert guidance to get the best deals with zero stress',
        'Verified properties and transparent transactions'
      ]
    },
    {
      icon: <Building2 className="w-12 h-12" />,
      title: 'Property Management',
      description: 'Perfect for NRIs, investors, and busy owners. Think of us as your personal property caretaker.',
      features: [
        'Regular property inspections & updates',
        'Tenant management, rent collection & upkeep',
        'Hassle-free maintenance handled by our team',
        'Peace of mind, even when you\'re miles away'
      ]
    },
    {
      icon: <Users className="w-12 h-12" />,
      title: 'Real Estate Consultancy',
      description: 'We do not just give advice – we guide you to profitable decisions.',
      features: [
        'Smart investment strategies tailored to your goals',
        'Market insights & location analysis',
        'Support for development, leasing, and resale opportunities',
        'Data-driven recommendations for maximum ROI'
      ]
    },
    {
      icon: <FileText className="w-12 h-12" />,
      title: 'Document & Approval Services',
      description: 'No more long queues, confusion, or paperwork stress – we handle it all.',
      features: [
        'Patta, EC, Chitta, FMB, and other land records',
        'Smooth DTCP & CMDA approval process',
        'Assistance with registration, mutation & property updates',
        'Complete documentation support from start to finish'
      ]
    },
    {
      icon: <Scale className="w-12 h-12" />,
      title: 'Legal Assistance for Property',
      description: 'Your property is safe when our legal experts are on your side.',
      features: [
        'Land dispute resolution & settlement support',
        'Title verification & due diligence for safe buying',
        'Expert legal partners for complex cases',
        'Protecting your ownership rights at every step'
      ]
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="bg-primary text-white py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-heading text-5xl md:text-6xl font-medium mb-6">Our Core Services</h1>
            <p className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto">
              Complete real estate solutions under one roof – from buying and selling to legal support and property management.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto space-y-16">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
            >
              <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                <div className="w-20 h-20 bg-secondary rounded-sm flex items-center justify-center text-primary mb-6">
                  {service.icon}
                </div>
                <h2 className="font-heading text-3xl md:text-4xl font-medium text-primary mb-4">
                  {service.title}
                </h2>
                <p className="text-lg text-[#57534E] leading-relaxed mb-6">
                  {service.description}
                </p>
                <ul className="space-y-3">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-[#57534E]">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                <div className="relative h-[400px] rounded-sm overflow-hidden">
                  <img
                    src={[
                      'https://images.unsplash.com/photo-1663756915301-2ba688e078cf?crop=entropy&cs=srgb&fm=jpg&q=85',
                      'https://images.unsplash.com/photo-1622015663319-e97e697503ee?crop=entropy&cs=srgb&fm=jpg&q=85',
                      'https://images.pexels.com/photos/8815915/pexels-photo-8815915.jpeg',
                      'https://images.unsplash.com/photo-1766740606233-6573571caa6b?crop=entropy&cs=srgb&fm=jpg&q=85',
                      'https://images.pexels.com/photos/7647411/pexels-photo-7647411.jpeg'
                    ][index]}
                    alt={service.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#FFFBF5]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          >
            <h2 className="font-heading text-4xl md:text-5xl font-medium text-primary mb-6">
              Why Choose Us?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left mb-8">
              {[
                'One-stop solution for everything in real estate',
                'Reliable, transparent, and customer-first approach',
                'Strong presence across Tamil Nadu',
                'Trusted by buyers, sellers, NRIs, and investors'
              ].map((point, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-[#57534E] text-lg">{point}</span>
                </div>
              ))}
            </div>
            <p className="text-xl text-primary font-medium mb-8">
              With us, you don't just buy or sell property – you gain peace of mind.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}